const { v4: uuidv4 } = require('uuid');

const getValueFromPath = (obj, path) => {
  if (!path) return obj;
  const keys = path.split('.');
  return keys.reduce((acc, key) => acc && acc[key], obj);
}

function validateField(type, value, allowNull = false, item) {
  // Handle null values first
  if (value === null) {
    // If type is an array and includes null, or allowNull is true
    return Array.isArray(type) ? (type.includes(null) || allowNull) : allowNull;
  }

  // If type is an array, check if value is in the allowed values
  if (Array.isArray(type)) {
    return type.includes(value);
  }

  // Check if type is valid
  if (!['string', 'number', 'boolean'].includes(type)) {
    throw new Error(`Invalid type specified: ${type}. Must be 'string', 'number', 'boolean', or an array of allowed values. Value is: ${value}. Item is: ${JSON.stringify(item)}`);
  }

  // Validate type matches
  return typeof value === type;
}

const findChanges = (original, modified, path = '') => {
  if (original === modified) return [];

  // Handle null/undefined cases
  if (!original || !modified) {
    return [{
      path,
      oldValue: original,
      newValue: modified
    }];
  }

  const changes = [];
  const allKeys = new Set([...Object.keys(original), ...Object.keys(modified)]);

  for (const key of allKeys) {
    const currentPath = path ? `${path}.${key}` : key;

    // Handle added properties
    if (!(key in original)) {
      changes.push({
        path: currentPath,
        oldValue: undefined,
        newValue: modified[key]
      });
      continue;
    }

    // Handle deleted properties
    if (!(key in modified)) {
      changes.push({
        path: currentPath,
        oldValue: original[key],
        newValue: undefined
      });
      continue;
    }

    const originalValue = original[key];
    const modifiedValue = modified[key];

    // Handle null values explicitly
    if (originalValue === null || modifiedValue === null) {
      if (originalValue !== modifiedValue) {
        changes.push({
          path: currentPath,
          oldValue: originalValue,
          newValue: modifiedValue
        });
      }
      continue;
    }

    // If both values are objects, recurse
    if (typeof originalValue === 'object' && typeof modifiedValue === 'object') {
      changes.push(...findChanges(originalValue, modifiedValue, currentPath));
      continue;
    }

    // Compare values directly
    if (originalValue !== modifiedValue) {
      changes.push({
        path: currentPath,
        oldValue: originalValue,
        newValue: modifiedValue
      });
    }
  }

  return changes;
}

const handleBaseChanges = (state, team_changes) => {
  if(team_changes) {
    return {
      home: { id: null, name: null },
      first: { id: null, name: null },
      second: { id: null, name: null },
      third: { id: null, name: null }
    }
  }

  let m = JSON.parse(JSON.stringify(state))
  if(m.base_changes.third_to_home) {
    m.bases.third = { id: null, name: null }
  }
  if(m.base_changes.second_to_third) {
    if (m.base_changes.second_to_third === "O") {
      m.bases.third = m.bases.second
    }
    m.bases.second = { id: null, name: null }
  }
  if(m.base_changes.first_to_second) {
    if (m.base_changes.first_to_second === "O") {
      m.bases.second = m.bases.first
    }
    m.bases.first = { id: null, name: null }
  }
  if(m.base_changes.home_to_first) {
    if (m.base_changes.home_to_first === "O") {
      m.bases.first = m.bases.home
    }
    m.bases.home = { id: null, name: null }
  }

  return m.bases
}

const getBaseIndexOfRunner = (state) => {
  // check the state.bases.id of each base and return the index of furthest non null value
  if(!state.bases) return null
  let base_index = null
  if(state.bases.home.id) base_index = 0
  if(state.bases.first.id) base_index =  1
  if(state.bases.second.id) base_index = 2
  if(state.bases.third.id) base_index = 3
  return base_index
}

const getRunner = state => {
  const bases = ['home', 'first', 'second', 'third']
  const runnerIndex = getBaseIndexOfRunner(state)
  const base = bases[runnerIndex]

  const name = state.bases[base].name
  const id = state.bases[base].id

  return { name, id }
}

const getNextHitter = (state, game_players, hitting, hitter_changes, team_changes) => {
  const players = game_players[hitting]
  const num = hitter_changes ? state[`${hitting}_hitter_num`] : state.hitter_number
  const hitter_num_numerolla = state[`${hitting}_hitter_num`]
  const joker_hat = state.joker_hitting
  let next_num
  if(num > 9) next_num = state[`${hitting}_hitter_num`] + 1
  else if(num === 9) next_num = 1
  else next_num = num + 1

  let final_num = hitter_changes && !team_changes ? next_num : num
  final_num = joker_hat && hitter_changes ? hitter_num_numerolla : final_num
  const player = players.find(p => p.num === final_num)

  return { id: player.id, name: player.player_name, num: player.num }
}

const getBasesStateFromBases = (e) => {
  const bases = e.bases
  // "0" - "1" - "1-2" - "1-3" - "0-3" - "0-2" - "2-3" - "Ajo"
  const h = !!bases.home.id
  const f = !!bases.first.id
  const s = !!bases.second.id
  const t = !!bases.third.id

  if(!h && !f && !s && !t) return null
  if(f && s && t) return "Ajo"
  if(!f && s && t) return "2-3"
  if(f && !s && t) return "1-3"
  if(!f && !s && t) return "0-3"
  if(!f && s && !t) return "0-2"
  if(f && s && !t) return "1-2"
  if(f && !s && !t) return "1"
  if(!f && !s && !t) return "0"
}

module.exports = {
  getBasesStateFromBases,
  findChanges,
  getBaseIndexOfRunner,
  outsIncoming: (state) => {
    const base_items = ["third", "second", "first", "home"];
    // check state.base_changes for any X
    if(!state.base_changes) return null
    let outs = 0
    base_items.forEach(base => {
      if(state.base_changes[base] === "X") outs++
    })
    return Math.min(outs, 3 - state.outs)
  },
  runsMade: (state) => {
    const kls = state.karkilyonnit
    let min = 0
    if(!state.base_changes.third_to_home && kls < 2) return 0
    if(state.base_changes.third_to_home === "O" && state.bases.third.id) min = 1

    if(!state) throw new Error('Invalid state');

    const base_items = ["third", "second", "first", "home"];

    let runs = 0
    for(let i = 0; i < kls; i++) {
      if(state.bases[base_items[i]].id) runs++
    }

    return Math.max(runs, min)
  },
  hitOutcome: (state) => {
    const base_items = ["third", "second", "first", "home"];
    const base_change_items = ['third_to_home', 'second_to_third', 'first_to_second', 'home_to_first'];

    if (!state) throw new Error('Invalid state');
    const { bases, base_changes } = state;
    if (!bases || !base_changes) throw new Error('Invalid bases or base_changes');

    let furthermost_found = false;
    let result = 'vapaa';  // Default result

    // Using for...of instead of forEach to allow for early returns
    for (let i = 0; i < base_items.length; i++) {
        const base = base_items[i];
        const change = base_changes[base_change_items[i]];
        const has_runner = !!bases[base].id;

        if (change && has_runner && furthermost_found) {
            // Someone behind the furthermost runner moved
            if (change === "X") return "takapalo";
            if (change === "H") return "haava_kt";
            return "takaeteneminen";
        }
        else if (change && has_runner && !furthermost_found) {
            // Furthermost runner moved
            if (change === "X") return "palo";
            if (change === "H") return "haava";
            return "karkilyonti";
        }

        if (has_runner) furthermost_found = true;
    }

    return result;  // Return 'vapaa' if no other conditions were met
  },
  getNewState: (oldState, gamePlayers, ranOut = false) => {
    let mod = JSON.parse(JSON.stringify(oldState))
    // gamePlayers = { home: [ { num: 4, player_name: "Moro", id: 123 } ], away: [] }
    // APUMUUTTUJA
    const half_inning_ends = ranOut || oldState.outs + oldState.outs_inflicted_this_hit >= 3 // should never be > 3 tho
    const hitter_changes = !!oldState.base_changes.home_to_first || half_inning_ends
    const full_inning_ends = half_inning_ends && oldState.game_phase.lopettava
    const jakso_ends =
      full_inning_ends
      &&
      (
        (
          oldState.game_phase.inning === 4
          && oldState.game_phase.lopettava
          && [1, 2].includes(oldState.game_phase.jakso)
        ) || (
          oldState.game_phase.jakso === 3
          && oldState.game_phase.lopettava
        )
      )

    const team_changes = half_inning_ends

    mod.hitting = !team_changes ? oldState.hitting : oldState.hitting === "home" ? "away" : "home"

    const new_hitting = half_inning_ends ? oldState.hitting === "home" ? "away" : "home" : oldState.hitting
    mod.hitting = new_hitting
    const next_hitter = getNextHitter(oldState, gamePlayers, new_hitting, hitter_changes, team_changes)

    if(team_changes) {
      mod.hitting_team_id = oldState.fielding_team_id
      mod.fielding_team_id = oldState.hitting_team_id
    }

    mod.starttime = null
    mod.endtime = null
    mod.nypyt = 0
    mod.karkilyonnit = 0
    mod.hit_number =
      hitter_changes
      ? 1
      : oldState.was_wrong
        ? oldState.hit_number
        : oldState.hit_number + 1
    mod.runs_created_this_hit = 0
    mod.outs_inflicted_this_hit = 0
    mod.marked_laiton = false
    mod.after_marked_laiton = oldState.marked_laiton
    mod.marked = false
    mod.karpanen = false
    mod.vapaataival = false
    mod.lapilyonti = false
    mod.pitch_happened = true
    mod.hit_outcome = 'vapaa'
    mod.vvo = false
    //
    mod.vaara_alla = ( !hitter_changes && oldState.was_wrong) || ( !hitter_changes && oldState.vaara_alla )
    mod.was_wrong = false
    mod.base_changes = {
      home_to_first: null,
      first_to_second: null,
      second_to_third: null,
      third_to_home: null
    }
    mod.bases = handleBaseChanges(oldState, team_changes)

    mod.bases.home = {
      id: next_hitter.id,
      name: next_hitter.name
    }

    const runner = getRunner(mod, gamePlayers)
    mod.karkirunner_id = runner.id
    mod.karkirunner_name = runner.name

    mod.previous_hit_id = oldState.event_id

    mod.hitter_number = next_hitter.num
    mod.hitter_id = next_hitter.id
    mod.hitter_name = next_hitter.name

    mod.joker_hitting = mod.hitter_number > 9

    if(jakso_ends) {
      mod.game_phase = {
        jakso: Math.min(oldState.game_phase.jakso + 1, 4),
        inning: 1,
        lopettava: false
      }
    } else if(full_inning_ends) {
      mod.game_phase = {
        ...mod.game_phase,
        lopettava: false,
        inning: mod.game_phase.inning + 1
      }
    } else if(half_inning_ends) {
      mod.game_phase = {
        ...mod.game_phase,
        lopettava: true
      }
    }

    if(half_inning_ends) {
      mod.outs = 0
    } else {
      mod.outs = oldState.outs + oldState.outs_inflicted_this_hit
    }

    mod.runs_created_this_inning_before_hit = oldState.runs_created_this_hit + oldState.runs_created_this_inning_before_hit
    if(half_inning_ends) mod.runs_created_this_inning_before_hit = 0
    mod.outs_inflicted_plate_appearance_before = oldState.outs_inflicted_plate_appearance_before + oldState.outs_inflicted_this_hit
    if(hitter_changes) mod.outs_inflicted_plate_appearance_before = 0

    mod.event_id = null

    const next_num = next_hitter.num
    if(mod.hitting === "home" && !ranOut) {
      mod.home_hitter_num = next_num > 9 ? mod.home_hitter_num : next_num
    } else if(!ranOut) {
      mod.away_hitter_num = next_num > 9 ? mod.away_hitter_num : next_num
    }
    mod.plate_appearance_id = hitter_changes ? uuidv4() : oldState.plate_appearance_id
    mod.bases_state = getBasesStateFromBases(mod)

    const pitcher = gamePlayers[mod.hitting === 'home' ? 'away' : 'home'].find(p => p.pos === "L")
    mod.pitcher_id = pitcher.id
    mod.pitcher_name = pitcher.player_name

    const hitter = gamePlayers[mod.hitting].find(p => p.id === mod.bases?.home.id)
    if(hitter_changes) {
      mod.hitter_side = hitter.hits_from_left === null ? null : hitter?.hits_from_left ? "L" : "R"
    } else {
      mod.hitter_side = oldState.hitter_side
    }

    mod.phase_done = 1

    return mod
  },
  handleBaseChanges,
  getValueFromPath,
  validateField
}